import { Column, Entity, Index } from 'typeorm';
import { BaseEntity } from './base.entity';

/**
 * Which customer-facing documents this rule copies people on (PD-15003).
 *
 * Modelled as a column rather than one table per document so adding
 * statements or order confirmations later is a row, not a migration.
 */
export enum CopyDocumentType {
	INVOICE = 'invoice',
	DELIVERY_RECEIPT = 'delivery_receipt',
	/**
	 * PD-15079 — a credit memo emailed from the credit memo screen. Its own
	 * type so a tenant can route credits differently (they are money going
	 * OUT), but the resolver falls back to the INVOICE rules when no
	 * credit-memo rules are configured, so no tenant has to configure the
	 * same recipients twice.
	 */
	CREDIT_MEMO = 'credit_memo',
}

/** Openly copied (CC) or blind (BCC). */
export enum CopyMode {
	CC = 'cc',
	BCC = 'bcc',
}

/**
 * PD-15003 — who at the TENANT gets copied when a customer-facing document is
 * emailed out, configured per tenant.
 *
 * Before this, copying a colleague meant typing their address into the send
 * dialog every time, and the automatic delivery receipt had no way to copy
 * anyone at all.
 *
 * A rule names exactly one recipient by one of three means, in the order the
 * resolver prefers them:
 *
 *   - `fkRbacRoleId` — every active member of a role ("copy Accounting").
 *     Membership is resolved at send time, so staff changes need no edit here.
 *   - `fkUserId` — one named person, for someone who should be copied
 *     regardless of their role.
 *   - `emailAddress` — a plain address. Needed because a shared mailbox
 *     (accounting@…) is usually not a system user at all, and because a role
 *     can be empty: on Motor City Ice the Accounting role has zero members
 *     while Admin has eight, so a role-only design would let someone configure
 *     a rule that silently copies nobody.
 *
 * Exactly one of the three is set per row; the others are null.
 */
@Index('idx_document_copy_recipient_document', ['documentType', 'isActive'], {})
@Entity('document_copy_recipient')
export class DocumentCopyRecipientEntity extends BaseEntity {
	@Column('varchar', { name: 'documentType', length: 40 })
	public documentType!: CopyDocumentType;

	@Column('varchar', { name: 'copyMode', length: 10, default: CopyMode.BCC })
	public copyMode!: CopyMode;

	/** FK to _rbac_role._id — every active member is copied. */
	@Column('int', { name: 'fk__rbac_role__id', unsigned: true, nullable: true })
	public fkRbacRoleId!: number | null;

	/** FK to user._id — one named person. */
	@Column('int', { name: 'fk_user__id', unsigned: true, nullable: true })
	public fkUserId!: number | null;

	/** A plain address, for shared mailboxes and non-users. */
	@Column('varchar', { name: 'emailAddress', length: 255, nullable: true })
	public emailAddress!: string | null;

	@Column('tinyint', { name: 'isActive', width: 1, default: 1 })
	public isActive!: boolean;
}
